import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Building2, AlertTriangle } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function BrokerLive() {
  const [adapters, setAdapters] = useState(null);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const a = await api.brokerAdapters();
      setAdapters(a);
      if (a.oanda_live) {
        const [acc, pos] = await Promise.all([
          fetch(`${BASE}/api/broker/oanda/account`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` }
          }).then((r) => r.json()),
          fetch(`${BASE}/api/broker/oanda/positions`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` }
          }).then((r) => r.json()),
        ]);
        if (acc.detail) throw new Error(acc.detail);
        setAccount(acc);
        setPositions(pos.positions || []);
      }
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const oandaAdapter = adapters?.adapters?.oanda;

  return (
    <div className="p-6 space-y-6" data-testid="broker-live-page">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">Broker Live — OANDA Practice</h1>
          <p className="text-white/50 text-sm mt-1">Compte demo réel OANDA v20 REST API — vraies exécutions Forex sans risque.</p>
        </div>
        <button className="btn" onClick={load} disabled={loading} data-testid="broker-refresh">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Actualiser
        </button>
      </div>

      {adapters && !adapters.oanda_live ? (
        <div className="panel p-6" data-testid="oanda-setup-panel">
          <div className="flex items-start gap-3">
            <AlertTriangle size={24} className="text-warn flex-shrink-0" />
            <div>
              <div className="font-heading text-lg font-semibold">OANDA non configuré</div>
              <p className="text-sm text-white/70 mt-2">
                Pour activer l'exécution live sur un vrai compte de courtage :
              </p>
              <ol className="text-sm text-white/70 mt-3 space-y-2 pl-5 list-decimal">
                <li>Créer un compte gratuit OANDA Practice : <a href="https://www.oanda.com/register/#/sign-up/demo" target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline" data-testid="oanda-signup-link">https://www.oanda.com/register/#/sign-up/demo</a></li>
                <li>Récupérer un <b>API Token</b> depuis <a href="https://www.oanda.com/demo-account/tpa/personal_token" target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">Personal Access Token</a></li>
                <li>Trouver ton <b>Account ID</b> dans le dashboard OANDA</li>
                <li>Ajouter dans <code className="bg-white/10 px-1 rounded">/app/backend/.env</code> :
                  <pre className="mt-1 p-2 bg-white/5 rounded text-xs font-mono overflow-x-auto">
{`OANDA_API_TOKEN=your_token_here
OANDA_ACCOUNT_ID=xxx-xxx-xxxxxxxx-001
OANDA_ENVIRONMENT=practice`}
                  </pre>
                </li>
                <li>Redémarrer le backend : <code className="bg-white/10 px-1 rounded">sudo supervisorctl restart backend</code></li>
              </ol>
              <div className="mt-4 text-xs text-white/50">
                Le tier <b>Institutional</b> ($299/mo) débloque ce panel automatiquement. Tier <b>API</b> ($999/mo) débloque en plus l'exécution automatisée depuis les signaux du dashboard.
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {error && (
        <div className="panel p-4 border-red-500/30 text-red-400 text-sm" data-testid="broker-error">{error}</div>
      )}

      {account && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="oanda-account-stats">
            <Stat label="NAV (Equity)" value={`$${account.equity?.toFixed(2)}`} highlight />
            <Stat label="Balance" value={`$${account.balance?.toFixed(2)}`} />
            <Stat label="Unrealized PnL" value={`$${account.unrealized_pnl?.toFixed(2)}`}
                  color={account.unrealized_pnl >= 0 ? "up" : "down"} />
            <Stat label="Margin Used" value={`$${account.margin_used?.toFixed(2)}`} />
            <Stat label="Margin Avail." value={`$${account.margin_available?.toFixed(2)}`} />
            <Stat label="Positions ouvertes" value={account.n_open_positions} />
            <Stat label="Trades ouverts" value={account.n_open_trades} />
            <Stat label="Environment" value={account.environment} />
          </div>

          <div className="panel p-5">
            <div className="panel-title mb-3"><Building2 size={14} className="inline mr-2" />Positions OANDA</div>
            {positions.length === 0 ? (
              <div className="text-white/40 text-sm">Aucune position ouverte.</div>
            ) : (
              <table className="w-full text-sm" data-testid="oanda-positions-table">
                <thead><tr className="text-white/50 text-xs">
                  <th className="text-left py-2">Symbole</th><th>Côté</th>
                  <th className="text-right">Qté</th><th className="text-right">Prix moyen</th>
                  <th className="text-right">PnL non réalisé</th>
                </tr></thead>
                <tbody>
                  {positions.map((p, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="py-2 font-mono">{p.symbol}</td>
                      <td className={p.side === "BUY" ? "text-up" : "text-down"}>{p.side}</td>
                      <td className="text-right">{p.qty}</td>
                      <td className="text-right font-mono">{p.avg_price?.toFixed(5)}</td>
                      <td className={`text-right font-mono ${p.unrealized_pnl >= 0 ? "text-up" : "text-down"}`}>${p.unrealized_pnl?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, color, highlight }) {
  return (
    <div className={`p-3 rounded ${highlight ? "bg-accent-blue/10 border border-accent-blue/30" : "bg-white/5"}`}>
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-mono text-lg mt-1 font-bold ${color === "up" ? "text-up" : color === "down" ? "text-down" : highlight ? "text-accent-blue" : ""}`}>{value}</div>
    </div>
  );
}
